import { useCallback, useMemo, useState } from 'react';
import { CellProps, Column } from 'react-table';
import {
  WorkTaskStatus,
  WorkTaskListItemDTO3,
  TaskListResponseDTO,
  OrderByColumnEnum,
  BaseWorkTaskTypeEnum,
  TaskListRequestDTO
} from '../../../api/api';
import StatusTag from '../../../components/status-tag/StatusTag';
import useTableInstance from '../../../hooks/useTableInstance';
import { formatDate, sortDateTime } from '../../../utils/dateHandling';
import { getEnumDisplayValue } from '../../../utils/enumUtils';
import { getLocationString } from '../../../utils/location/locationHandling';
import { SearchColumnFilter, SelectColumnFilter, DateRangeColumnFilter } from './../task-list-view/TaskListFilterUtils';
import { dateRangeFilterFn, Cell, multiSelectFilterFn } from '../../../blocks/table/TableFilterUtils';
import { TableTab } from '../../../blocks/table/TableUtils';
import { Tab, Tabs } from '../../../blocks/table/TableServer';
import styled, { useTheme } from 'styled-components';
import { N1LightTheme } from '../../../theme/light-theme';
import { useDispatch, useSelector } from 'react-redux';
import { selectTechnicianFilterValues, setFilterValues } from '../../../stateManagement/reducers/taskListReducer';
import { LayerIcon } from '../../../assets/icons/LayerIcon';
import { technicianBaseFilter } from './TechnicianTaskList';
import { getUserEmail } from '../../../utils/authProvider/authProvider';
import { getWorkPermitTaskTypeId, getWorkPermitTechnicianDepartmentId } from '../../../utils/constants';

export const TechnicianTabs = {
  Mine: { header: 'Mine opgaver', id: -1 },
  WorkPermits: { header: 'Arbejdstilladelser (KOS)', id: -3 },
  All: { header: 'Alle opgaver', id: -2 }
};

// The active tab follows the filters of the last fetch, so it stays correct after "Fjern filter" and when
// returning to the list.
const getTechnicianTabId = (filterValues?: TaskListRequestDTO) => {
  if (!filterValues || filterValues.assignedToTechnicianEmailFilter) return TechnicianTabs.Mine.id;
  if (filterValues.createdByEmailFilter) return TechnicianTabs.WorkPermits.id;
  return TechnicianTabs.All.id;
};

export enum ColumnAccessors {
  Id = 'id',
  TaskType = 'taskType',
  Location = 'location',
  AssetId = 'assetId',
  Status = 'status',
  ProjectNumber = 'projectNumber',
  CompletedAt = 'completedAt'
}

export interface TaskData {
  id: string;
  taskType: string;
  location: string;
  assetId: string;
  status: string;
  projectNumber: string;
  completedAt?: string;
  baseWorkTaskType?: BaseWorkTaskTypeEnum;
}

export const useTechnicianTaskList = (tasks: WorkTaskListItemDTO3[]) => {
  const [tableConfig, setTableConfig] = useState<TaskListResponseDTO>({} as TaskListResponseDTO);

  const data = useMemo(() => {
    if (!tasks) return [];

    return tasks.map((task) => {
      return {
        id: task.id ?? '-',
        taskType: task.taskTypeName ?? '-',
        location: task.taskLocation ? getLocationString(task.taskLocation) : '-',
        assetId: task.assetId ?? '-',
        status: task.status ? getEnumDisplayValue(WorkTaskStatus[task.status]) : '-',
        projectNumber: task.projectNumber ?? '-',
        completedAt: task.completedDate,
        baseWorkTaskType: task.baseWorkTaskType
      } as TaskData;
    });
  }, [tasks]);

  const columns = useMemo(() => {
    return [
      {
        Header: getEnumDisplayValue(OrderByColumnEnum.WorkTaskId),
        accessor: ColumnAccessors.Id,
        width: 160,
        minWidth: 160,
        maxWidth: 160,
        Filter: SearchColumnFilter,
        filter: 'text',
        Cell: (cellProps: CellProps<TaskData>) => {
          const { cell } = cellProps;
          return <Cell>{cell.value}</Cell>;
        }
      },
      {
        Header: getEnumDisplayValue(OrderByColumnEnum.Type),
        accessor: ColumnAccessors.TaskType,
        width: 150,
        minWidth: 150,
        Filter: (_tableInstance) => SelectColumnFilter(_tableInstance, false),
        filter: multiSelectFilterFn,
        Cell: (cellProps: CellProps<TaskData>) => {
          const { cell } = cellProps;
          return <Cell>{cell.value}</Cell>;
        }
      },
      {
        Header: getEnumDisplayValue(OrderByColumnEnum.AssetId),
        accessor: ColumnAccessors.AssetId,
        width: 125,
        minWidth: 125,
        Filter: SearchColumnFilter,
        filter: 'text'
      },
      {
        Header: getEnumDisplayValue(OrderByColumnEnum.ProjectNumber),
        accessor: ColumnAccessors.ProjectNumber,
        width: 175,
        minWidth: 175,
        Filter: SearchColumnFilter,
        filter: 'text'
      },
      {
        Header: getEnumDisplayValue(OrderByColumnEnum.TaskLocation),
        accessor: ColumnAccessors.Location,
        width: 150,
        minWidth: 150,
        Filter: SearchColumnFilter,
        filter: 'text'
      },
      {
        Header: getEnumDisplayValue(OrderByColumnEnum.Status),
        accessor: ColumnAccessors.Status,
        width: 125,
        minWidth: 125,
        Filter: SelectColumnFilter,
        Cell: (cellProps: CellProps<TaskData>) => {
          const { cell } = cellProps;
          return <StatusTag statusValue={cell.value}>{cell.value}</StatusTag>;
        },
        filter: multiSelectFilterFn
      },
      {
        Header: getEnumDisplayValue(OrderByColumnEnum.CompletedDate),
        accessor: ColumnAccessors.CompletedAt,
        width: 175,
        minWidth: 175,
        Filter: (_tableInstance, x) => DateRangeColumnFilter(_tableInstance, true),
        filter: dateRangeFilterFn,
        sortType: (a, b) => sortDateTime(a.values.completedAt, b.values.plannedArrival),
        Cell: (cellProps: CellProps<TaskData>) => {
          const { cell } = cellProps;
          return <div>{cell.value ? formatDate(new Date(cell.value)) ?? '-' : '-'}</div>;
        }
      }
    ] as Column<TaskData>[];
  }, []);

  const workPermitTaskTypeId = getWorkPermitTaskTypeId();

  const taskListTabs: TableTab[] = useMemo(() => {
    let tabs: TableTab[] = [
      {
        id: TechnicianTabs.Mine.id,
        header: TechnicianTabs.Mine.header,
        filter: () => {},
        disabledColumns: []
      }
    ];

    if (!isNaN(workPermitTaskTypeId)) {
      tabs.push({
        id: TechnicianTabs.WorkPermits.id,
        header: TechnicianTabs.WorkPermits.header,
        filter: () => {},
        // The tab itself filters on task type and status, so the column filters must not overwrite it
        disabledColumns: [ColumnAccessors.TaskType, ColumnAccessors.Status]
      });
    }

    tabs.push({
      id: TechnicianTabs.All.id,
      header: TechnicianTabs.All.header,
      filter: () => {},
      disabledColumns: []
    });

    return tabs;
  }, [workPermitTaskTypeId]);

  const technicianFilterValues = useSelector(selectTechnicianFilterValues);
  const activeTab = useMemo(
    () => taskListTabs.find((tab) => tab.id === getTechnicianTabId(technicianFilterValues)),
    [taskListTabs, technicianFilterValues]
  );
  const userEmail = getUserEmail();
  const theme = useTheme() as N1LightTheme;
  const dispatch = useDispatch();

  const changeTab = useCallback(
    (tabId: number) => {
      let changeTabValues: TaskListRequestDTO;

      if (tabId === TechnicianTabs.All.id) {
        changeTabValues = {
          ...technicianBaseFilter,
          tagId: undefined
        };
      } else if (tabId === TechnicianTabs.WorkPermits.id) {
        changeTabValues = {
          ...technicianBaseFilter,
          taskTypesFilter: [workPermitTaskTypeId],
          taskStatusFilter: [WorkTaskStatus.Created],
          departmentIdFilter: [getWorkPermitTechnicianDepartmentId()],
          createdByEmailFilter: userEmail
        };
      } else {
        changeTabValues = {
          ...technicianBaseFilter,
          assignedToTechnicianEmailFilter: userEmail
        };
      }

      dispatch(setFilterValues(changeTabValues));
    },
    [dispatch, userEmail, workPermitTaskTypeId]
  );

  const tabs = useMemo(() => {
    return (
      <>
        {taskListTabs?.length && (
          <Tabs>
            {taskListTabs.map((tab) => {
              const active = activeTab?.header === tab.header;
              const tabTextColor = active ? theme.palette.text.primary : theme.palette.grey.black60;
              const isAllTasks = tab?.id === TechnicianTabs.All.id;
              return (
                <Tab
                  active={active}
                  textColor={tabTextColor}
                  onClick={() => changeTab(tab.id)}
                  key={tab.header}
                  data-testid={`list-tab-${tab.id}`}
                >
                  {tab?.header && (
                    <h4>
                      {isAllTasks && <StyledLayerIcon color={tabTextColor} size="18px" />} {tab?.header}
                    </h4>
                  )}
                </Tab>
              );
            })}
          </Tabs>
        )}
      </>
    );
  }, [activeTab?.header, changeTab, taskListTabs, theme.palette.grey.black60, theme.palette.text.primary]);

  const tableInstance = useTableInstance<TaskData>(data, columns, {
    initialState: {
      pageSize: 25,
      pageIndex: 0
    }
  });

  return {
    tableInstance,
    tableConfig,
    setTableConfig,
    tabs,
    activeTab
  };
};

const StyledLayerIcon = styled(LayerIcon)`
  margin-right: ${(props) => props.theme.spacing(3)};
`;
